import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ListTodo,
  AlertTriangle,
  CalendarClock,
  FileWarning,
  Users,
  FileText,
  ArrowUpRight,
} from "lucide-react";
import { formatDate, dueColor, relTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setData(data));
  }, []);

  const stats = data?.stats;

  const kpis = [
    { key: "my_today", label: "Today's Tasks", value: stats?.my_today, icon: CalendarClock, testid: "kpi-today" },
    { key: "my_overdue", label: "My Overdue", value: stats?.my_overdue, icon: AlertTriangle, tone: "danger", testid: "kpi-overdue" },
    { key: "my_open", label: "My Open Tasks", value: stats?.my_open, icon: ListTodo, testid: "kpi-open" },
    { key: "pending_docs", label: "Pending Documents", value: stats?.pending_docs, icon: FileWarning, testid: "kpi-pending-docs" },
    { key: "total_clients", label: "Active Clients", value: stats?.total_clients, icon: Users, testid: "kpi-clients" },
    { key: "total_docs", label: "Documents", value: stats?.total_docs, icon: FileText, testid: "kpi-docs" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Good {greeting()}, {user?.full_name?.split(" ")[0]}</h2>
        <p className="text-sm text-muted-foreground mt-1">Here&rsquo;s your firm at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <Card
            key={k.key}
            className="p-4 border-border shadow-none hover:bg-muted/40 transition-colors"
            data-testid={k.testid}
          >
            <div className="flex items-start justify-between mb-3">
              <k.icon size={15} className={cn("text-muted-foreground", k.tone === "danger" && "text-red-500")} />
            </div>
            {data ? (
              <div className={cn("font-mono text-2xl font-semibold", k.tone === "danger" && k.value > 0 && "text-red-600 dark:text-red-400")}>
                {k.value ?? 0}
              </div>
            ) : (
              <Skeleton className="h-7 w-10" />
            )}
            <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Today" testid="section-today">
          <TaskList tasks={data?.today_tasks} empty="Nothing due today." />
        </Section>
        <Section title="Overdue" testid="section-overdue">
          <TaskList tasks={data?.overdue_tasks} empty="Nothing overdue. Good work." tone="danger" />
        </Section>
        <Section title="Upcoming (7 days)" testid="section-upcoming">
          <TaskList tasks={data?.upcoming_tasks} empty="No upcoming deadlines." />
        </Section>
      </div>

      <Section title="Recent activity" testid="section-activity" right={<Link to="/activity" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>}>
        <div className="divide-y divide-border">
          {(data?.recent_activity || []).slice(0, 8).map((a) => (
            <div key={a.id} className="py-2.5 flex items-center gap-3 text-sm">
              <span className="font-medium">{a.actor_name}</span>
              <span className="text-muted-foreground">{a.action}</span>
              <span className="text-muted-foreground">{a.entity_type}</span>
              {a.entity_label && <span className="font-medium truncate">{a.entity_label}</span>}
              <span className="ml-auto text-xs text-muted-foreground font-mono">{relTime(a.created_at)}</span>
            </div>
          ))}
          {data?.recent_activity?.length === 0 && (
            <div className="py-6 text-sm text-muted-foreground text-center">No activity yet.</div>
          )}
        </div>
      </Section>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function Section({ title, children, right, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-sm font-semibold tracking-tight uppercase text-muted-foreground">{title}</h3>
        {right}
      </div>
      <Card className="border-border shadow-none">
        <div className="p-4">{children}</div>
      </Card>
    </div>
  );
}

function TaskList({ tasks, empty, tone }) {
  if (!Array.isArray(tasks)) return <Skeleton className="h-24 w-full" />;
  if (tasks.length === 0) return <div className="py-4 text-sm text-muted-foreground text-center">{empty}</div>;
  return (
    <div className="divide-y divide-border">
      {tasks.map((t) => (
        <Link
          to={`/tasks?id=${t.id}`}
          key={t.id}
          className="py-2 flex items-center gap-2 hover:bg-muted/40 -mx-2 px-2 rounded transition-colors"
          data-testid={`dashboard-task-${t.id}`}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", tone === "danger" ? "bg-red-500" : "bg-foreground/60")} />
          <span className="text-sm font-medium truncate flex-1">{t.title}</span>
          {t.due_date && (
            <span className={cn("text-xs font-mono", dueColor(t.due_date))}>{formatDate(t.due_date, "dd MMM")}</span>
          )}
        </Link>
      ))}
    </div>
  );
}
